import { useCallback } from 'react'
import { Cargando, Problema, Vacio } from '../componentes/Estados.js'
import { Icono } from '../componentes/Icono.js'
import { MesaDelJugador } from '../componentes/MesaDelJugador.js'
import { MiPareja } from '../componentes/MiPareja.js'
import { MiPosicion } from '../componentes/MiPosicion.js'
import { PastillaTorneo } from '../componentes/Pastilla.js'
import { useCarga } from '../carga.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { Jugador, MiMesa, MiTorneo } from '../datos/index.js'
import { cuantosCompiten, fechaLarga } from '../formato.js'
import { ir } from '../navegacion.js'

/**
 * La pantalla de hoy.
 *
 * Está armada alrededor de un solo momento: el jugador saca el teléfono en el
 * salón, entre dos rondas, para saber en qué mesa le toca. Todo lo demás
 * —próximos torneos, historial— está más abajo o en otra pestaña, porque en ese
 * momento no le importa.
 *
 * Si no hay nada jugándose, la pantalla no se queda vacía disculpándose: pasa a
 * mostrar lo que viene, que es la única otra cosa que puede querer saber quien
 * entra un martes.
 */

interface Datos {
  torneos: MiTorneo[]
  enCurso: MiTorneo | null
  mesas: MiMesa[]
}

/**
 * La mesa que importa ahora: la de la ronda que no se cerró.
 *
 * Cuando todas están cerradas —el momento entre que el organizador cierra una
 * ronda y arma la siguiente— devuelve la última jugada. Es deliberado: dejar la
 * tarjeta vacía en ese hueco haría que la web parezca rota justo cuando más
 * gente la mira.
 */
function mesaDelMomento(mesas: MiMesa[]): { mesa: MiMesa; esperando: boolean } | null {
  const abierta = mesas.find((m) => m.rondaEstado !== 'cerrada')
  if (abierta) return { mesa: abierta, esperando: false }
  const ultima = mesas[mesas.length - 1]
  return ultima ? { mesa: ultima, esperando: true } : null
}

export function Inicio({ jugador }: { jugador: Jugador }) {
  const pedir = useCallback(async (): Promise<Datos> => {
    const repo = await obtenerRepositorio()
    // Solo lo que se juega y lo que viene: los jugados son del historial.
    const torneos = await repo.listarMisTorneos(['en_curso', 'inscripcion'])
    const enCurso = torneos.find((t) => t.estado === 'en_curso') ?? null
    const mesas = enCurso ? await repo.listarMisMesas(enCurso.id) : []
    return { torneos, enCurso, mesas }
  }, [])

  const { datos, error, cargando, recargar } = useCarga(pedir)

  if (error) return <Problema mensaje={error} alReintentar={recargar} />
  if (!datos) return cargando ? <Cargando que="Buscando tus torneos…" /> : null

  const { torneos, enCurso, mesas } = datos
  const proximos = torneos.filter((t) => t.estado === 'inscripcion')
  const ahora = enCurso ? mesaDelMomento(mesas) : null

  return (
    <div className="pantalla">
      <header className="saludo">
        <h1>Hola, {jugador.apodo ?? jugador.nombre}</h1>
        {jugador.numero !== null && (
          <p className="tenue">
            Eres el jugador número <b>{jugador.numero}</b>
          </p>
        )}
      </header>

      {enCurso ? (
        <section className="seccion">
          {/* El título es el enlace al torneo: en el teléfono, un nombre con una
              flechita al lado es un blanco de medio centímetro, y el que está
              buscando su mesa lo va a apretar igual. Así todo el renglón entra
              en el área tocable, y el `h2` de afuera conserva el encabezado que
              usa quien navega con lector de pantalla. */}
          <h2 className="torneo-titulo">
            <button onClick={() => ir({ nombre: 'torneo', id: enCurso.id, pestana: 'mesa' })}>
              <span className="titulo-texto">
                <span className="titulo-nombre">{enCurso.nombre}</span>
                <span className="tenue">
                  Ronda {enCurso.rondaEnCurso ?? enCurso.rondasCerradas} de {enCurso.numeroRondas} ·{' '}
                  {cuantosCompiten(enCurso.participantes, enCurso.modalidad)}
                </span>
              </span>
              <Icono nombre="flecha" />
            </button>
          </h2>

          {/* La mesa y la posición van una debajo de la otra en el teléfono y
              lado a lado cuando hay ancho. Son las dos preguntas del que está
              jugando, y en una pantalla grande contestarlas de a una y dejar
              media pantalla vacía obliga a bajar para nada. Quién manda lo
              decide la hoja de estilo: acá solo quedan agrupadas. */}
          <div className="columnas">
            <div className="columna-mayor">
              {ahora ? (
                <>
                  {ahora.esperando && (
                    <p className="nota">
                      La ronda {ahora.mesa.rondaNumero} está cerrada y la próxima todavía no se
                      armó. Esta fue tu última mesa.
                    </p>
                  )}
                  <MesaDelJugador
                    mesa={ahora.mesa}
                    destacada
                    {...(!ahora.esperando &&
                    enCurso.cargaParcial !== 'desactivada' &&
                    ahora.mesa.tipo === 'normal'
                      ? {
                          alAnotar: () =>
                            ir({ nombre: 'anotar', id: enCurso.id, mesaId: ahora.mesa.mesaId }),
                        }
                      : {})}
                  />
                </>
              ) : (
                <Vacio titulo="Todavía no se armó la primera ronda">
                  <p>Cuando el organizador la arme, tu mesa aparece acá.</p>
                </Vacio>
              )}
            </div>

            {/* La pareja arriba de la posición: en Parejas es la identidad con la
                que se juega, y el puesto de abajo es el de los dos. Las dos van
                en una sola columna para que la grilla de escritorio siga siendo
                de dos, mesa a la izquierda y lo mío a la derecha. */}
            {enCurso.pareja ? (
              <div className="columna-menor">
                <MiPareja torneo={enCurso} />
                {enCurso.mi && <MiPosicion torneo={enCurso} />}
              </div>
            ) : (
              enCurso.mi && <MiPosicion torneo={enCurso} />
            )}
          </div>
        </section>
      ) : (
        <section className="seccion">
          <Vacio titulo="No tienes ningún torneo jugándose">
            <p>
              Cuando arranque uno en el que estés anotado, tu mesa y tu posición van a aparecer acá.
            </p>
          </Vacio>
        </section>
      )}

      {proximos.length > 0 && (
        <section className="seccion">
          <h3 className="seccion-titulo">Lo que viene</h3>
          <ul className="lista-torneos">
            {proximos.map((t) => (
              <li key={t.id}>
                <button onClick={() => ir({ nombre: 'torneo', id: t.id, pestana: 'mesa' })}>
                  <span className="lista-principal">
                    <b>{t.nombre}</b>
                    <span className="tenue">{fechaLarga(t.fecha)}</span>
                  </span>
                  <PastillaTorneo estado={t.estado} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
